import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `governance` em português do Brasil. Declarado, nunca inferido, para que uma falta quebre o
 * `tsc`.
 *
 * ── As classes de risco ──
 *
 * São as MESMAS seis palavras de `approvals`, `trace`, `activity` e `admin.studio`, porque o
 * inglês é o mesmo mapa nas cinco (uma ideia, uma palavra). Concordam no feminino com a *ação*
 * classificada — que é também o que a ferramenta (f.) e a capacidade (f.) por trás dela são —,
 * e o fallback "sem classificação" evita o gênero por completo (PRD-27 D-21).
 *
 * ── "Quem", não "Ator" ──
 *
 * A coluna `actor` é quem fez a coisa. *Ator* em português é ofício de teatro antes de ser
 * termo de auditoria, e a coluna é estreita: *Quem* é mais curto, mais claro e é o que
 * alguém diria lendo a tabela em voz alta.
 *
 * ── O que NÃO está aqui ──
 *
 * `action` e `resourceType` continuam sendo os identificadores verbatim que o servidor
 * grava, em inglês, de propósito (D-8, LOC-043): quem audita quer exatamente o token que
 * está na tabela, e traduzi-lo seria perder a única coisa que aquela coluna serve para
 * fazer.
 */
export const governance: Messages['governance'] = {
  title: 'Governança',
  lead: 'Ações aguardando decisão, e o registro imutável do que aconteceu.',

  approvalsTab: 'Aprovações',
  auditTrail: 'Trilha de auditoria',

  empty: {
    title: 'Nada aguardando',
    body: 'Quando o assistente propõe algo consequente — enviar uma mensagem, alterar um sistema de registro — ele para aqui até uma pessoa decidir.'
  },

  queueTitle: 'Aguardando decisão',
  queueHint: 'cada uma é decidida na sua própria conversa',

  risk: {
    read: 'leitura',
    reversible_write: 'escrita reversível',
    external_communication: 'comunicação externa',
    privileged: 'privilegiada',
    financial: 'financeira',
    destructive: 'destrutiva'
  },
  irreversible: 'irreversível',

  /**
   * *Aprovação* é feminino, então "solicitada". Sem preposição: `formatAgo` já traz a dela.
   * E dois-pontos no prazo, como em `approvals.expires`: `formatUntil` devolve a palavra
   * "expirado" quando o instante já passou, e "expira expirado" não é frase (LOC-022).
   */
  requestedAndExpires: (requested, expires) => `solicitada ${requested} · prazo: ${expires}`,
  reviewInConversation: 'Revisar na conversa',

  auditLoading: 'Carregando eventos de auditoria…',
  auditFailedTitle: 'Não foi possível carregar a trilha de auditoria',
  auditFailedBody:
    'Isto é um problema para ler o registro, não um registro sem nada dentro. Tente de novo em instantes; se continuar falhando, avise quem cuida desta instalação.',
  auditEmpty: {
    title: 'Nenhum evento de auditoria',
    body: 'Nada foi registrado ainda.'
  },

  /** O particípio concorda com *eventos* (m.) e o zero fica no singular: "0 exibido". */
  shown: (fmt, count) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} exibido`
      : `${fmt.formatCount(count)} exibidos`,

  columns: {
    when: 'Quando',
    actor: 'Quem',
    action: 'Ação',
    resource: 'Recurso',
    outcome: 'Resultado'
  },
  outcome: {
    allow: 'permitido',
    deny: 'negado',
    error: 'erro'
  }
}
